/*
 * COPYRIGHT 2023 Jobvite, Inc. All rights reserved. This copyright notice is Copyright Management
 * Information under 17 USC 1202 and is included to protect this work and deter copyright
 * infringement. Removal or alteration of this Copyright Management Information without the express
 * written permission of Jobvite, Inc. is prohibited, and any such unauthorized removal or
 * alteration will be a violation of federal law.
 */

import React, { FC, useState } from 'react';
import { useMultipleLocalizedText } from '../../../../contexts/Localization/localizationHooks';
import BusinessUnits from '../businessUnit/BusinessUnits';
import Departments from '../departments/Departments';
import Locations from '../locations/Locations';
import { Box } from '@pixel/core';
import { ContentModal } from '@pixel/core/ContentModal';
import {
    AddDimensionModalProps,
 } from '../../OdsTypes';
import { getLocalStorageDimensionData, setLocalStorageDimensionData } from '../../OdsTransform';

export const AddDimensionModal: FC<AddDimensionModalProps> = ({ 
    modalState,
    setAddDimensionModalState: setModalState,
    addSelectedDimensions,
    dimension,
    accessDrawerData,
    selectedLocationForAPIPayload,
    setSelectedLocationForAPIPayload,
    selectedDepartmentForAPIPayload,
    setSelectedDepartmentForAPIPayload,
    selectedBusinessUnitForAPIPayload,
    setSelectedBusinessUnitForAPIPayload,
    showInfoMessage,
    setShowInfoMessage
 }) => {
    const [currentDimensionSelectionList, setCurrentDimensionSelectionList] = useState({});

    const {
        ACCESS_GROUP_ADD_BUSINESS_UNITS_MODAL_TITLE,
        ACCESS_GROUP_ADD_BUSINESS_UNITS_BUTTON_CAPTION,
        ACCESS_GROUP_ADD_DEPARTMENTS_MODAL_TITLE,
        ACCESS_GROUP_ADD_DEPARTMENTS_BUTTON_CAPTION,
        ACCESS_GROUP_ADD_LOCATIONS_MODAL_TITLE,
        ACCESS_GROUP_ADD_LOCATIONS_BUTTON_CAPTION,
        ODS_CANCEL_BUTTON_CAPTION,
    } = useMultipleLocalizedText()

    type DimensionSettings = {
        MODAL_TITLE: string;
        BUTTON_CAPTION: string;
        CONTEXT: any;
    };

    const MODAL_DIMENSION_SETTINGS: { [key: string]: DimensionSettings } = {
        BUSINESS_UNIT: {
            MODAL_TITLE: ACCESS_GROUP_ADD_BUSINESS_UNITS_MODAL_TITLE,
            BUTTON_CAPTION: ACCESS_GROUP_ADD_BUSINESS_UNITS_BUTTON_CAPTION,
            CONTEXT: <BusinessUnits 
            accessGroupData={accessDrawerData}
            selectedBusinessUnitForAPIPayload={selectedBusinessUnitForAPIPayload}
            setSelectedBusinessUnitForAPIPayload={setSelectedBusinessUnitForAPIPayload}
            setCurrentDimensionSelectionList={setCurrentDimensionSelectionList}
            />
        },
        DEPARTMENT: {
            MODAL_TITLE: ACCESS_GROUP_ADD_DEPARTMENTS_MODAL_TITLE,
            BUTTON_CAPTION: ACCESS_GROUP_ADD_DEPARTMENTS_BUTTON_CAPTION,
            CONTEXT: <Departments 
            accessGroupData={accessDrawerData}
            selectedDepartmentForAPIPayload={selectedDepartmentForAPIPayload}
            setSelectedDepartmentForAPIPayload={setSelectedDepartmentForAPIPayload}
            setCurrentDimensionSelectionList={setCurrentDimensionSelectionList}
            />
        },
        LOCATION: {
            MODAL_TITLE: ACCESS_GROUP_ADD_LOCATIONS_MODAL_TITLE,
            BUTTON_CAPTION: ACCESS_GROUP_ADD_LOCATIONS_BUTTON_CAPTION,
            CONTEXT: <Locations 
            accessGroupData={accessDrawerData} 
            selectedLocationForAPIPayload={selectedLocationForAPIPayload} 
            setSelectedLocationForAPIPayload={setSelectedLocationForAPIPayload}
            setCurrentDimensionSelectionList={setCurrentDimensionSelectionList}
            />
        }
    };

    const isLocationsModalOpen = MODAL_DIMENSION_SETTINGS[dimension].BUTTON_CAPTION === ACCESS_GROUP_ADD_LOCATIONS_BUTTON_CAPTION;
    const isDepartmentsModalOpen = MODAL_DIMENSION_SETTINGS[dimension].BUTTON_CAPTION === ACCESS_GROUP_ADD_DEPARTMENTS_BUTTON_CAPTION;

    const handleOnModalEnter = () => {
        if (isLocationsModalOpen) {
            setCurrentDimensionSelectionList(getLocalStorageDimensionData('AG_location_list'));
        } else if (isDepartmentsModalOpen) {
            setCurrentDimensionSelectionList(getLocalStorageDimensionData('AG_department_list'));
        } else {
            setCurrentDimensionSelectionList(getLocalStorageDimensionData('AG_businessUnit_list'));
        }
    }

    const onAddActionCancelled = () => {
        if (isLocationsModalOpen) {
            setLocalStorageDimensionData('AG_location_list', currentDimensionSelectionList);
        } else if (isDepartmentsModalOpen) {
            setLocalStorageDimensionData('AG_department_list', currentDimensionSelectionList);
        } else {
            setLocalStorageDimensionData('AG_businessUnit_list', currentDimensionSelectionList);
        }
        
        setSelectedLocationForAPIPayload([]);
        setSelectedDepartmentForAPIPayload([]);
        setSelectedBusinessUnitForAPIPayload([]);
    }

    const onAddDimension = () => {
        if(MODAL_DIMENSION_SETTINGS[dimension].BUTTON_CAPTION === ACCESS_GROUP_ADD_LOCATIONS_BUTTON_CAPTION){
            setShowInfoMessage({ ...showInfoMessage, locationInfoMessage: true });
        } else if(MODAL_DIMENSION_SETTINGS[dimension].BUTTON_CAPTION === ACCESS_GROUP_ADD_DEPARTMENTS_BUTTON_CAPTION){
            setShowInfoMessage({ ...showInfoMessage, departmentInfoMessage: true });
        } else {
            setShowInfoMessage({ ...showInfoMessage, businessUnitInfoMessage: true });
        }
        setModalState();
        return true;
    }

    const handleClose = () => {
        setModalState();
        onAddActionCancelled();
    }

    return (
        <ContentModal
            id="add-candidate"
            open={modalState}
            onClose={handleClose}
            showClose={true}
            sizeVariant="xl"
            title={MODAL_DIMENSION_SETTINGS[dimension].MODAL_TITLE}
            rejectActions={[{ reason: "cancel", label: ODS_CANCEL_BUTTON_CAPTION }]}
            resolveActions={[{ reason: "ok", label: MODAL_DIMENSION_SETTINGS[dimension].BUTTON_CAPTION }]}
            onResolve={onAddDimension}
            onReject={onAddActionCancelled}
            autoscroll={false}
            labelCloseAria='close'
            onExited={() => console.log('exited')}
            onEntered={handleOnModalEnter}
        >
            <Box className='access-group-add-dimension-modal'>
                {MODAL_DIMENSION_SETTINGS[dimension].CONTEXT}
            </Box>
        </ContentModal>
    );
}